import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
   Quick Connect — single-row form for low-friction lead capture.
   Mirrors the secondary CTA used on /about's "Ready to build?"
   section so any "Start a Project" CTA on the site can pair with
   the same path: name what you need + drop an email, we respond
   in 12 hours.

   Props are optional — `caption` overrides the small uppercase
   label above the form, and `className` lets the consumer scope
   width / alignment to its container.
   ────────────────────────────────────────────────────────────── */

type Props = {
  caption?: string;
  className?: string;
  /** Form Type sent to Web3Forms — useful when filtering by source location. */
  source?: string;
};

const QuickConnectForm: React.FC<Props> = ({
  caption = "Or quick connect — we'll be in touch in 12 hours",
  className = '',
  source = 'Quick Connect — 12hr Response',
}) => {
  const [email, setEmail] = useState('');
  const [need, setNeed] = useState('');
  const [emailError, setEmailError] = useState('');
  const [needError, setNeedError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    setEmailError('');
  };

  const handleNeedChange = (val: string) => {
    setNeed(val);
    if (val.trim() && needError) setNeedError('');
  };

  const handleSubmit = async () => {
    let hasError = false;
    if (!need.trim()) {
      setNeedError('Please tell us what you need.');
      hasError = true;
    }
    if (!email) {
      setEmailError('Please enter your email.');
      hasError = true;
    } else if (emailError) {
      hasError = true;
    }
    if (hasError || submitting) return;

    setSubmitting(true);
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
          subject: `Quick Connect — ${need.trim()} — ${email}`,
          from_name: 'Voqals Quick Connect (12hr)',
          replyto: email,
          Email: email,
          'What They Need': need.trim(),
          'Form Type': source,
        }),
      });
      // Fire Google Ads conversion event so this form's submissions count
      // alongside the other conversion-tracked forms (ProjectBrief, etc.).
      window.gtag?.('event', 'conversion', {
        send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD',
        value: 1.0,
        currency: 'INR',
      });
      setDone(true);
    } catch { setDone(true); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className={`flex items-center gap-2 text-white/85 text-[10px] md:text-[clamp(10px,0.7vw,13px)] uppercase tracking-[0.22em] font-bold ${className}`}>
        <Check className="w-[clamp(12px,1vw,15px)] h-[clamp(12px,1vw,15px)] text-[#56CFFC]" />
        We'll reach out within 12 hours
      </div>
    );
  }

  const hasError = !!(needError || emailError);

  return (
    <div className={`flex flex-col items-center gap-[clamp(10px,0.7vw,12px)] w-full max-w-[clamp(360px,62vw,860px)] ${className}`}>
      <span className="text-white/55 text-[10px] md:text-[clamp(9px,0.65vw,12px)] uppercase tracking-[0.2em] font-bold text-center">
        {caption}
      </span>

      {/* Mobile layout: stacked pills (subject + email + full-width gradient submit) */}
      <div className="md:hidden w-full flex flex-col gap-2.5">
        <input
          type="text"
          placeholder="What you need (e.g. Indian TTS data)"
          value={need}
          onChange={(e) => handleNeedChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          disabled={submitting}
          maxLength={120}
          required
          className={`w-full bg-white/[0.04] backdrop-blur-md border ${needError ? 'border-red-400/60' : 'border-white/15 focus:border-[#56CFFC]/60'} rounded-full px-5 py-3.5 text-white text-[13px] outline-none placeholder:text-white/40 disabled:opacity-60 transition-colors`}
        />
        <input
          type="email"
          placeholder="Your email"
          value={email}
          onChange={(e) => validateEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          disabled={submitting}
          required
          className={`w-full bg-white/[0.04] backdrop-blur-md border ${emailError ? 'border-red-400/60' : 'border-white/15 focus:border-[#56CFFC]/60'} rounded-full px-5 py-3.5 text-white text-[13px] outline-none placeholder:text-white/40 disabled:opacity-60 transition-colors`}
        />
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="group w-full mt-1 py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] disabled:opacity-50 text-white rounded-full font-bold text-[11px] tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-2.5 shadow-[0_0_20px_rgba(27,54,255,0.3)]"
        >
          {submitting ? 'Sending…' : 'Get In Touch'}
          {!submitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
        </button>
      </div>

      {/* Desktop layout: single horizontal pill */}
      <div className={`hidden md:flex items-stretch w-full rounded-full overflow-hidden border ${hasError ? 'border-red-400/60' : 'border-white/15 focus-within:border-[#56CFFC]/60'} bg-white/[0.03] backdrop-blur-md transition-colors`}>
        <input
          type="text"
          placeholder="What you need (e.g. Indian TTS data)"
          value={need}
          onChange={(e) => handleNeedChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          disabled={submitting}
          maxLength={120}
          required
          className="bg-transparent pl-[clamp(16px,1.2vw,22px)] pr-[clamp(10px,0.85vw,14px)] py-[clamp(10px,0.85vw,14px)] text-white text-[clamp(11px,0.85vw,14px)] outline-none flex-[2.4] min-w-[clamp(220px,22vw,300px)] placeholder:text-white/35 disabled:opacity-60"
        />
        <div className="w-px bg-white/15 my-[clamp(8px,0.7vw,12px)] shrink-0" />
        <input
          type="email"
          placeholder="Your email"
          value={email}
          onChange={(e) => validateEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          disabled={submitting}
          required
          className="bg-transparent px-[clamp(10px,0.85vw,14px)] py-[clamp(10px,0.85vw,14px)] text-white text-[clamp(11px,0.85vw,14px)] outline-none flex-1 min-w-0 placeholder:text-white/35 disabled:opacity-60"
        />
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="group bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-white/10 text-white px-[clamp(16px,1.3vw,24px)] py-[clamp(10px,0.85vw,14px)] text-[clamp(10px,0.7vw,13px)] font-bold uppercase tracking-[0.18em] transition-colors flex items-center gap-2 whitespace-nowrap shrink-0"
        >
          {submitting ? 'Sending…' : 'Get In Touch'}
          {!submitting && <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />}
        </button>
      </div>

      {hasError && (
        <span className="text-red-400/90 text-[10px] md:text-[clamp(9px,0.65vw,12px)] text-center">
          {needError || emailError}
        </span>
      )}
    </div>
  );
};

export default QuickConnectForm;
